import { Index } from "@agtech/core/utilities/AgTechUtilities"
import { IAgTechGridItem } from "./Data/AgTechGridData"

export type AgTechGridEntitySelection<TEntity> = IAgTechGridItem & {
    entity: TEntity,
    isSelected: boolean,
    wasOriginallySelected: boolean
}

export type AgTechGridEntitySelectionIndex<TEntity extends IAgTechGridItem> = {
    [id: string | number]: AgTechGridEntitySelection<TEntity>
}

export const buildGridEntitySelections = <TEntity extends IAgTechGridItem>(props: {
    entities: TEntity[],
    isSelected: (entity: TEntity) => boolean,
    wasOriginallySelected?: (entity: TEntity) => boolean
}): AgTechGridEntitySelection<TEntity>[] => {
    return props.entities.map(entity => ({
        id: entity.id,
        entity: entity,
        isSelected: props.isSelected(entity),
        wasOriginallySelected: props.wasOriginallySelected?.(entity) ?? props.isSelected(entity)
    }));
}

export const mergeGridEntitySelections = <TEntity extends IAgTechGridItem>(props: {
    entitySelections: AgTechGridEntitySelectionIndex<TEntity>,
    entities: TEntity[]
}): AgTechGridEntitySelection<TEntity>[] => {
    const selectedEntitiesNotInList = Object.values(props.entitySelections)
        .filter(e => !props.entities.some(entity => entity.id === e.id));

    const allEntities = [
        ...props.entities,
        ...selectedEntitiesNotInList.map(e => e.entity)
    ];
    
    return buildGridEntitySelections({
        entities: allEntities,
        isSelected: entity => props.entitySelections[entity.id]?.isSelected ?? false,
        wasOriginallySelected: entity => props.entitySelections[entity.id]?.wasOriginallySelected ?? false
    })
}

export const buildGridEntitySelectionIndex = <TEntity extends IAgTechGridItem>(props: {
    entities: TEntity[],
    isSelected: (entity: TEntity) => boolean,
    wasOriginallySelected?: (entity: TEntity) => boolean
}): AgTechGridEntitySelectionIndex<TEntity> => {
    const entitySelections = buildGridEntitySelections(props);
    return Index(entitySelections, e => e.id);
}

export const markGridSelectionEntityAsSelected = <TEntity extends IAgTechGridItem>(props: {
    entity: TEntity,
    selections: AgTechGridEntitySelectionIndex<TEntity>
}): AgTechGridEntitySelectionIndex<TEntity> => {
    return {
        ...props.selections,
        [props.entity.id]: {
            id: props.entity.id,
            entity: props.entity,
            isSelected: true,
            wasOriginallySelected: props.selections[props.entity.id]?.wasOriginallySelected ?? false
        }
    }
}

export const markGridSelectionEntityAsUnselected = <TEntity extends IAgTechGridItem>(props: {
    entity: TEntity,
    selections: AgTechGridEntitySelectionIndex<TEntity>
}): AgTechGridEntitySelectionIndex<TEntity> => {
    return {
        ...props.selections,
        [props.entity.id]: {
            id: props.entity.id,
            entity: props.entity,
            isSelected: false,
            wasOriginallySelected: props.selections[props.entity.id]?.wasOriginallySelected ?? false
        }
    }
}